import React, { useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FaEdit } from "react-icons/fa";
import axios from "axios";
import { toast } from "react-toastify";
import Grid from "../components/Grid";
import { saveAs } from "file-saver";
import ExcelJS from "exceljs";
import { FaDownload } from "react-icons/fa";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function Suppliers({ onAddSupplierClick }) {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [userRole, setUserRole] = useState("");
  const [selectedSearchField, setSelectedSearchField] = useState("supplier_name");
  

  useEffect(() => {
    const fetchUserRole = async () => {
      const token = sessionStorage.getItem("token");
      if (!token) return toast.error("No auth token found.");

      try {
        const res = await axios.get(`${API_BASE_URL}/users/specific`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setUserRole(res.data.role);
      } catch (err) {
        toast.error("Failed to fetch user role.");
      }
    };

    const fetchSuppliers = async () => {
      try{
        const token = sessionStorage.getItem("token"); // Retrieve token from sessionStorage
        if (!token) {
          toast.error("Authentication token is missing. Please log in again.");
          return;
        }

        const response = await axios.get(`${API_BASE_URL}/suppliers/get`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setSuppliers(response.data.suppliers);

      }catch(error){
        console.error("Error fetching suppliers:", error);
        toast.error("Error fetching suppliers. Please try again.");
      }
    };

    fetchSuppliers();
    fetchUserRole();
  }, []);

  const handleDownloadExcel = async () => {
  const dataToExport = searchTerm.trim().length > 0
    ? suppliers.filter((s) =>
        s[selectedSearchField]?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    : suppliers;

    if (dataToExport.length === 0) {
      toast.warn("No supplier data to export.");
      return;
    }

    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet("Suppliers");

    const headerRow = [
      "Supplier Code",
      "Supplier Name",
      "Supplier Address",
      "Contact Person",
      "BRN",
      "Mobile No",
      "Email",
      "Status",
      ...(userRole === "Main Admin" ? ["Organization"] : []),
    ];

    worksheet.addRow(headerRow);

    const header = worksheet.getRow(1);
    header.eachCell((cell) => {
      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "FFD966" },
      };
      cell.font = { bold: true };
      cell.border = {
        top: { style: "thin" },
        left: { style: "thin" },
        bottom: { style: "thin" },
        right: { style: "thin" },
      };
    });

    dataToExport.forEach((supplier) => {
      const row = [
        supplier.supplier_code || "N/A",
        supplier.supplier_name || "N/A",
        supplier.supplier_address || "N/A",
        supplier.Contact_person || "N/A",
        supplier.BRN || "N/A",
        supplier.mobile_no || "N/A",
        supplier.email || "N/A",
        supplier.status || "N/A",
      ];

      if (userRole === "Main Admin") {
        row.push(supplier.organization?.organization_name || "N/A");
      }

      worksheet.addRow(row);
    });

    worksheet.columns.forEach((column) => {
      column.width = column.header
        ? column.header.toString().length < 20
          ? 20
          : column.header.toString().length
        : 20;
    });

    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(blob, "suppliers.xlsx");
  };

  const columns = [
    { field: "supplier_code", headerName: "ID", flex: 1 },
    { field: "supplier_name", headerName: "Supplier Name", flex: 1 },
    { field: "supplier_address", headerName: "Supplier Address", flex: 1 },
    { field: "Contact_person", headerName: "Contact Person", flex: 1 },
    { field: "BRN", headerName: "BRN", flex: 1 },
    { field: "mobile_no", headerName: "Mobile No", flex: 1 },
    { field: "email", headerName: "Email", flex: 1 },
    {
      field: "status",
      headerName: "Status",
      flex: 1,
      renderCell: (params) => {
        const status = params.row.status;
        const statusClass =
          status === "Active"
            ? "bg-[#388E3C] text-white"
            : "bg-[#E57373] text-white";

        return (
          <span className={`px-2 py-1 rounded-sm ${statusClass}`}>
            {status}
          </span>
        );
      },
    },
    {
      field: "action",
      headerName: "Action",
      flex: 1,
      renderCell: (params) => (
        <div className="flex justify-left items-left w-10 h-10">
          <button
          //onClick={() => handleSupplierClick(params.row)}
          className="bg-[#28ad4b] text-white px-2 py-1 flex items-center rounded-lg"
        >
          <FaEdit className="text-white mr-2" />
          Edit
        </button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex h-screen">
      <div className="flex-1 p-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-green-700">Suppliers</h1>
          {userRole === "Company Admin" && (
            <button
              onClick={onAddSupplierClick}
              className="bg-green-700 text-white px-4 py-2 rounded-lg"
            >
              Add Supplier
            </button>
          )}
        </div>

        <div className="bg-white p-4 rounded-lg shadow">
          <div className="flex justify-between mb-4 items-center flex-wrap gap-4">
            {/* Left Section: Search filters */}
            <div className="flex items-center flex-wrap gap-4">
              <select
                className="px-4 py-2 border border-gray-300 rounded-lg"
                value={selectedSearchField}
                onChange={(e) => setSelectedSearchField(e.target.value)}
              >
                <option value="supplier_name">Supplier Name</option>
                <option value="supplier_code">Supplier Code</option>
                <option value="email">Email</option>
                <option value="mobile_no">Mobile No</option>
                <option value="BRN">BRN</option>
                <option value="Contact_person">Contact Person</option>
                <option value="status">Status</option>
                <option value="supplier_address">Supplier Address</option>
              </select>

              <div className="relative">
                <input
                  type="text"
                  placeholder="Search suppliers..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-4 py-2 pl-10 border border-gray-300 rounded-lg w-full max-w-xs"
                />
                <div className="absolute inset-y-0 left-0 flex items-center pl-3">
                  <FontAwesomeIcon icon={faSearch} className="text-gray-500" />
                </div>
              </div>
            </div>

            {/* Right Section: Download Excel */}
            <div>
              <button
                onClick={handleDownloadExcel}
                className="flex items-center bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700"
              >
                <FaDownload className="mr-2" />
                Download Excel
              </button>
            </div>
          </div>          

          <Grid
            rows={suppliers.filter((s) => {
              const fieldValue = s[selectedSearchField];
              return fieldValue?.toString().toLowerCase().includes(searchTerm.toLowerCase());
            })}
            columns={columns}
            getRowId={(row) => row.supplier_code}
          />
        </div>

        {selectedSupplier && (
          <div className="fixed inset-0 flex items-center justify-center bg-gray-900 bg-opacity-50 z-50">
            <div className="bg-white p-8 rounded-lg shadow-lg w-full md:w-2/3 lg:w-1/2 relative">
              <button
                onClick={() => setSelectedSupplier(null)}
                className="absolute top-2 right-2 bg-red-500 text-white w-8 h-8 flex justify-center items-center rounded-sm hover:bg-red-700"
              >
                &times;
              </button>

              <h2 className="text-xl font-semibold mb-4">{selectedSupplier.supplier_code}</h2>

              <div className="mb-4">
                <label className="block text-gray-700">Supplier Name</label>
                <input
                  type="text"
                  value={selectedSupplier.supplier_name}
                  onChange={(e) =>
                    setSelectedSupplier({ ...selectedSupplier, supplier_name: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700">Supplier Address</label>
                <input
                  type="text"
                  value={selectedSupplier.supplier_address}
                  onChange={(e) =>
                    setSelectedSupplier({ ...selectedSupplier, supplier_address: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700">Mobile No</label>
                <input
                  type="text"
                  value={selectedSupplier.mobile_no}
                  onChange={(e) =>
                    setSelectedSupplier({ ...selectedSupplier, mobile_no: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700">Email</label>
                <input
                  type="email"
                  value={selectedSupplier.email}
                  onChange={(e) =>
                    setSelectedSupplier({ ...selectedSupplier, email: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700">Supplier Type</label>
                <input
                  type="text"
                  value={selectedSupplier.supplier_type}
                  onChange={(e) =>
                    setSelectedSupplier({ ...selectedSupplier, supplier_type: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="mb-4">
                <label className="block text-gray-700">Status</label>
                <select
                  value={selectedSupplier.status}
                  onChange={(e) =>
                    setSelectedSupplier({ ...selectedSupplier, status: e.target.value })
                  }
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg"
                >
                  <option value="">Select Status</option>
                  <option value="Active">Active</option>
                  <option value="Inactive">Inactive</option>
                </select>
              </div>

              <div className="flex justify-center">
                <button
                  //onClick={handleUpdateSupplier}
                  className="bg-blue-500 text-white px-4 py-2 rounded-lg"
                >
                  Update
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Suppliers;
